/**
 * DecisionFlow — PlanTypeSelector
 * ui/src/components/tenant-admin/PlanTypeSelector.jsx
 * S685 — Plan type governance foundation
 *
 * Purpose:
 *   CA selects a base plan type when creating or modifying a plan component.
 *   Selection writes plan_component_modifier_applicability rows via
 *   POST /api/v1/plan-components/{id}/set-plan-type, which governs:
 *     - Which modifier cards in PlanDetailView are required (must configure)
 *     - Which are optional
 *     - Which are hidden (not_applicable)
 *     - What the submit gate checks
 *
 * API:
 *   GET  /api/v1/plan-types                                     — catalog with entitlement
 *   POST /api/v1/plan-components/{id}/set-plan-type             — write applicability rows
 *
 * Two modes:
 *   mode='select'  — CA picks from catalog cards (initial plan type selection)
 *   mode='change'  — CA adds/removes modules from existing plan type (custom composition)
 *
 * Props:
 *   planComponentId  — int — required — the component to govern
 *   currentPlanType  — string | null — current plan_type_key if already set
 *   onComplete       — fn({ planTypeKey, applicabilityRows }) — called after successful save
 *   onCancel         — fn() — called on cancel
 *   readOnly         — bool — show current type without allowing change
 *
 * Architecture:
 *   - useAuth() for user — ARCH-2
 *   - apiGet / apiPost from client.js
 *   - No tenant_id in request body
 *   - CSS via rgb(var(--color-*)) tokens only
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../auth/AuthContext.jsx';
import { apiGet, apiPost } from '../../api/client';

// ---------------------------------------------------------------------------
// Industry icon map (emoji fallback — no external dependency)
// ---------------------------------------------------------------------------
const INDUSTRY_ICONS = {
  'SaaS':                   '☁️',
  'Cloud':                  '☁️',
  'Technology':             '💻',
  'Professional Services':  '💼',
  'Manufacturing':          '🏭',
  'Financial Services':     '🏦',
  'Real Estate':            '🏢',
  'Distribution':           '📦',
  'Pharmaceuticals':        '💊',
  'Medical Devices':        '🔬',
  'Biotech':                '🧬',
  'Telecommunications':     '📡',
  'CPG':                    '🛒',
  'Pharma':                 '💊',
  'Field Sales':            '🗺️',
  'Inside Sales':           '📞',
};

const SALES_MOTION_LABELS = {
  new_logo:  'New Logo',
  expand:    'Expand',
  retain:    'Retain',
  team:      'Team',
  specialty: 'Specialty',
};

// CSS
const btnPrimary =
  'rounded-lg bg-[rgb(var(--color-primary))] px-4 py-2 text-tok-sm font-semibold ' +
  'text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50 disabled:cursor-not-allowed';

const btnSecondary =
  'rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] ' +
  'px-4 py-2 text-tok-sm font-medium text-[rgb(var(--color-text))] ' +
  'hover:bg-[rgb(var(--color-surface-muted))]';

// ---------------------------------------------------------------------------
// ModulePill — shows a single module with applicability badge
// ---------------------------------------------------------------------------
function ModulePill({ module, applicability }) {
  const colors = {
    required:       'bg-[rgb(var(--color-primary))]/10 text-[rgb(var(--color-primary))] border-[rgb(var(--color-primary))]/20',
    optional:       'bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))] border-[rgb(var(--color-border))]',
    not_applicable: 'bg-[rgb(var(--color-border))]/30 text-[rgb(var(--color-text-muted))]/50 border-transparent line-through',
  };
  const labels = { required: 'Required', optional: 'Optional', not_applicable: 'N/A' };

  return (
    <div className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-medium ${colors[applicability] ?? colors.optional}`}>
      <span>{module.module_name}</span>
      {!module.entitled && applicability !== 'not_applicable' && (
        <span className="text-[rgb(var(--color-warning))]" title="Not available on your tier">🔒</span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PlanTypeCard — single plan type option
// ---------------------------------------------------------------------------
function PlanTypeCard({ planType, selected, onSelect }) {
  const isAvailable = planType.available;
  const requiredModules = planType.modules_required.filter(m => !m.is_weight_card);
  const optionalModules = planType.modules_optional.filter(m => !m.is_weight_card).slice(0, 4);
  const moreCount = Math.max(0, planType.modules_optional.filter(m => !m.is_weight_card).length - 4);

  return (
    <button
      type="button"
      disabled={!isAvailable}
      onClick={() => isAvailable && onSelect(planType.plan_type_key)}
      className={[
        'w-full text-left rounded-xl border p-4 transition-all',
        'focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-focus-ring))]',
        selected
          ? 'border-[rgb(var(--color-primary))] bg-[rgb(var(--color-primary))]/5 ring-2 ring-[rgb(var(--color-primary))]/30'
          : isAvailable
          ? 'border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] hover:border-[rgb(var(--color-primary))]/40 hover:bg-[rgb(var(--color-surface-muted))]'
          : 'border-[rgb(var(--color-border))]/50 bg-[rgb(var(--color-surface-muted))]/50 opacity-60 cursor-not-allowed',
      ].join(' ')}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">
              {planType.label}
            </span>
            {selected && (
              <span className="rounded-full bg-[rgb(var(--color-primary))] px-2 py-0.5 text-[10px] font-semibold text-white">
                Selected
              </span>
            )}
            {!isAvailable && (
              <span className="rounded-full bg-[rgb(var(--color-warning))]/10 px-2 py-0.5 text-[10px] font-medium text-[rgb(var(--color-warning))]">
                Upgrade required
              </span>
            )}
          </div>

          <p className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))] leading-relaxed line-clamp-2">
            {planType.description}
          </p>

          {/* Sales motions */}
          <div className="mt-2 flex flex-wrap gap-1">
            {planType.sales_motions.map(sm => (
              <span
                key={sm}
                className="rounded-full border border-[rgb(var(--color-border))] px-2 py-0.5 text-[10px] text-[rgb(var(--color-text-muted))]"
              >
                {SALES_MOTION_LABELS[sm] ?? sm}
              </span>
            ))}
          </div>

          {/* Industries */}
          <div className="mt-1.5 text-[10px] text-[rgb(var(--color-text-muted))]">
            {planType.industries.slice(0, 3).map(ind => (
              <span key={ind}>{INDUSTRY_ICONS[ind] ?? '🏢'} {ind} </span>
            ))}
            {planType.industries.length > 3 && (
              <span>+{planType.industries.length - 3} more</span>
            )}
          </div>
        </div>
      </div>

      {/* Module composition */}
      <div className="mt-3 space-y-1.5">
        {requiredModules.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {requiredModules.map(m => (
              <ModulePill key={m.module_code} module={m} applicability="required" />
            ))}
          </div>
        )}
        {optionalModules.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {optionalModules.map(m => (
              <ModulePill key={m.module_code} module={m} applicability="optional" />
            ))}
            {moreCount > 0 && (
              <span className="text-[10px] text-[rgb(var(--color-text-muted))] self-center">
                +{moreCount} optional
              </span>
            )}
          </div>
        )}
      </div>

      {!isAvailable && planType.unavailable_reason && (
        <p className="mt-2 text-[10px] text-[rgb(var(--color-warning))]">
          {planType.unavailable_reason}
        </p>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function PlanTypeSelector({
  planComponentId,
  currentPlanType = null,
  onComplete,
  onCancel,
  readOnly = false,
}) {
  const { user } = useAuth();

  const [planTypes, setPlanTypes]       = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');
  const [selectedKey, setSelectedKey]   = useState(currentPlanType);
  const [saving, setSaving]             = useState(false);
  const [saveError, setSaveError]       = useState('');
  const [tenantTier, setTenantTier]     = useState('');

  // Search/filter
  const [search, setSearch]             = useState('');
  const [filterIndustry, setFilterIndustry] = useState('');

  const loadPlanTypes = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError('');
    try {
      const data = await apiGet('/api/v1/plan-types');
      setPlanTypes(data.plan_types ?? []);
      setTenantTier(data.tenant_tier ?? '');
    } catch (err) {
      setError(err?.message || 'Unable to load plan types.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { loadPlanTypes(); }, [loadPlanTypes]);

  // Derive all industries for filter dropdown
  const allIndustries = Array.from(
    new Set(planTypes.flatMap(pt => pt.industries))
  ).sort();

  // Filter plan types
  const filtered = planTypes.filter(pt => {
    if (search.trim()) {
      const q = search.toLowerCase();
      if (
        !pt.label.toLowerCase().includes(q) &&
        !pt.description.toLowerCase().includes(q) &&
        !pt.industries.some(i => i.toLowerCase().includes(q))
      ) return false;
    }
    if (filterIndustry && !pt.industries.includes(filterIndustry)) return false;
    return true;
  });

  const handleSave = useCallback(async () => {
    if (!selectedKey) return;
    setSaving(true);
    setSaveError('');
    try {
      const result = await apiPost(
        `/api/v1/plan-components/${planComponentId}/set-plan-type`,
        { plan_type_key: selectedKey }
      );
      onComplete?.({
        planTypeKey:       selectedKey,
        applicabilityRows: result.applicability_rows,
        rowsUpserted:      result.rows_upserted,
      });
    } catch (err) {
      setSaveError(err?.message || 'Failed to save plan type. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [selectedKey, planComponentId, onComplete]);

  const selectedPlanType = planTypes.find(pt => pt.plan_type_key === selectedKey);

  return (
    <div className="flex flex-col gap-4">

      {/* Header */}
      <div>
        <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">
          {readOnly ? 'Plan Type' : 'Select Plan Type'}
        </h3>
        <p className="mt-0.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
          {readOnly
            ? 'The plan type determines which compensation modules are configured for this component.'
            : 'The plan type governs which compensation modules are required or optional for this component. You can add or remove specific modules after selecting a base type.'
          }
        </p>
        {tenantTier && (
          <span className="mt-1 inline-block rounded-full border border-[rgb(var(--color-border))] px-2 py-0.5 text-[10px] text-[rgb(var(--color-text-muted))]">
            Tenant tier: {tenantTier}
          </span>
        )}
      </div>

      {/* Current selection summary (when readOnly or already selected) */}
      {selectedPlanType && readOnly && (
        <div className="rounded-xl border border-[rgb(var(--color-primary))]/20 bg-[rgb(var(--color-primary))]/5 p-4">
          <div className="flex items-center gap-2">
            <span className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">
              {selectedPlanType.label}
            </span>
            <span className="rounded-full bg-[rgb(var(--color-primary))] px-2 py-0.5 text-[10px] font-semibold text-white">
              Active
            </span>
          </div>
          <p className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
            {selectedPlanType.description}
          </p>
          <div className="mt-2 flex flex-wrap gap-1">
            {selectedPlanType.modules_required.filter(m => !m.is_weight_card).map(m => (
              <ModulePill key={m.module_code} module={m} applicability="required" />
            ))}
            {selectedPlanType.modules_optional.filter(m => !m.is_weight_card).slice(0, 3).map(m => (
              <ModulePill key={m.module_code} module={m} applicability="optional" />
            ))}
          </div>
        </div>
      )}

      {!readOnly && (
        <>
          {/* Search + filter */}
          <div className="flex flex-wrap gap-2">
            <input
              type="text"
              placeholder="Search plan types…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-focus-ring))] w-48"
            />
            <select
              value={filterIndustry}
              onChange={e => setFilterIndustry(e.target.value)}
              className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-focus-ring))]"
            >
              <option value="">All industries</option>
              {allIndustries.map(ind => (
                <option key={ind} value={ind}>{ind}</option>
              ))}
            </select>
          </div>

          {/* Error */}
          {error && (
            <div className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
              {error}
            </div>
          )}

          {/* Loading skeleton */}
          {loading && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[1,2,3,4].map(i => (
                <div key={i} className="h-40 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] animate-pulse" />
              ))}
            </div>
          )}

          {/* Plan type cards grid */}
          {!loading && filtered.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[480px] overflow-y-auto pr-1">
              {filtered.map(pt => (
                <PlanTypeCard
                  key={pt.plan_type_key}
                  planType={pt}
                  selected={selectedKey === pt.plan_type_key}
                  onSelect={setSelectedKey}
                />
              ))}
            </div>
          )}

          {!loading && filtered.length === 0 && !error && (
            <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-6 py-8 text-center">
              <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
                No plan types match your filters.
              </p>
              <button
                type="button"
                onClick={() => { setSearch(''); setFilterIndustry(''); }}
                className="mt-2 text-tok-xs text-[rgb(var(--color-primary))] hover:underline"
              >
                Clear filters
              </button>
            </div>
          )}

          {/* Selected plan type summary */}
          {selectedPlanType && (
            <div className="rounded-xl border border-[rgb(var(--color-success))]/30 bg-[rgb(var(--color-success))]/5 px-4 py-3">
              <p className="text-tok-xs font-semibold text-[rgb(var(--color-success))]">
                ✓ {selectedPlanType.label} selected
              </p>
              <p className="mt-0.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
                {selectedPlanType.modules_required.filter(m => !m.is_weight_card).length} required module{selectedPlanType.modules_required.filter(m => !m.is_weight_card).length !== 1 ? 's' : ''} · {selectedPlanType.modules_optional.filter(m => !m.is_weight_card).length} optional
              </p>
            </div>
          )}

          {/* Save error */}
          {saveError && (
            <div className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
              {saveError}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2 pt-1 border-t border-[rgb(var(--color-border))]">
            <button
              type="button"
              onClick={handleSave}
              disabled={!selectedKey || saving}
              className={btnPrimary}
            >
              {saving ? 'Saving…' : 'Set Plan Type'}
            </button>
            {onCancel && (
              <button type="button" onClick={onCancel} className={btnSecondary}>
                Cancel
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
